import * as THREE from "three/webgpu";
import type { NexusEvent } from "../../domain/mesh";
import type { SpatialProjectionPipeline } from "../spatial/SpatialProjectionPipeline";
import type { RelationPath, RelationPathProjector } from "./RelationPathProjector";

export class RelationPulseProjector {
  private event: NexusEvent | null = null;
  private path: RelationPath | null = null;
  private age = Number.POSITIVE_INFINITY;
  private reversed = false;
  private readonly semanticPoint = new THREE.Vector3();
  private readonly visualPoint = new THREE.Vector3();

  constructor(
    private readonly pulse: THREE.Mesh,
    private readonly relationProjector: RelationPathProjector,
    private readonly spatial: SpatialProjectionPipeline,
  ) {}

  set(event: NexusEvent | null, path: RelationPath | null, reversed = false) {
    this.event = event;
    this.path = path;
    this.reversed = reversed;
    this.age = event && path ? 0 : Number.POSITIVE_INFINITY;
    this.pulse.visible = Boolean(event && path);
  }

  update(
    delta: number,
    singularityPosition: THREE.Vector3,
    camera: THREE.PerspectiveCamera,
    viewportWidth: number,
    viewportHeight: number,
  ) {
    if (!this.event || !this.path || !this.pulse.visible) return;
    this.age += delta;
    const t = Math.min(this.age / 1.35, 1);
    const eased = t * t * (3 - 2 * t);
    const opticalPointBlend = this.relationProjector.sampleSemantic(
      this.path,
      this.reversed ? 1 - eased : eased,
      this.semanticPoint,
    );
    this.spatial.toVisualBlended(
      this.semanticPoint,
      singularityPosition,
      camera,
      viewportWidth,
      viewportHeight,
      this.visualPoint,
      "connector",
      "point",
      opticalPointBlend,
    );
    this.pulse.position.copy(this.visualPoint);
    const scale = 1 + Math.sin(t * Math.PI) * 0.85;
    this.pulse.scale.setScalar(scale);
    if (t >= 1) this.pulse.visible = false;
  }

  clear() {
    this.event = null;
    this.path = null;
    this.age = Number.POSITIVE_INFINITY;
    this.reversed = false;
    this.pulse.visible = false;
  }
}
